import type { LocalizedTemplateWithValues, RemediationReviewItem } from '@libs/types';

import { RemediationDecision, RemediationItemStatus } from '@libs/constants';

/**
 * A blank the AI left, and where it has to be filled.
 *
 * One entry per distinct token across the whole run, not per document: the same
 * `[DPO_CONTACT_EMAIL]` appears in every document that names a DPO, and asking
 * for it once per document would ask the same question five times and invite
 * five different answers.
 */
export interface PendingPlaceholder {
  token: string;
  label: string;
  /** Documents this blank appears in, by name. */
  documents: string[];
  /**
   * The organization-profile field this blank is asking for, when one matches.
   *
   * The AI leaves a placeholder precisely because the profile had no value for
   * it, so a match is the common case rather than the exception — and it is what
   * lets an answer given here be worth giving: written to the profile, the next
   * remediation fills the blank itself instead of asking again.
   */
  field: PlaceholderField | null;
}

/**
 * The profile field a blank maps to, with what the template says it holds.
 *
 * `type` and `options` come along because they are what makes the input right:
 * a date field asked for as free text gets typed back in six formats, and a
 * field with a fixed option set has exactly one correct control.
 */
export interface PlaceholderField {
  id: string;
  key: string;
  label: string;
  /** Template field type — `date`, `number`, `email`, `select`, `text`, … */
  type: string;
  /** Fixed choices, when the template constrains the value to a set. */
  options: string[] | null;
  currentValue: unknown;
  /**
   * The profile category that owns this field.
   *
   * Carried because a save is per-category: `PATCH /tenant-profile/manual-edits`
   * derives the target category from the field ids it is given and rejects a
   * body that spans more than one. A run's blanks come from wherever the
   * template happens to keep them, so writing them back is one request per
   * category — and that cannot be worked out without this.
   */
  categoryId: string;
}

/**
 * Field types a single typed-in answer can actually become.
 *
 * The fill card offers one text-ish control per blank, so the answer arrives as
 * a string. The API validates each value against its field's declared type and
 * rejects the save outright when the shape is wrong — so a blank matched to a
 * `table`, `file`, `multi_select` or list field is not something this page can
 * write to the profile, however well the names line up. Those still get asked
 * and still get substituted into the document; they just stop there.
 */
const PROFILE_SAVABLE_TYPES = new Set([
  'string',
  'textarea',
  'email',
  'phone',
  'date',
  'select',
  'number',
  'boolean',
]);

/** Whether an answer to this blank can be written back to the profile at all. */
export function isProfileSavable(field: PlaceholderField | null | undefined): boolean {
  return !!field && PROFILE_SAVABLE_TYPES.has(field.type);
}

/**
 * The typed answer as the field's declared type, or `null` when it cannot be
 * one.
 *
 * `number` and `boolean` fields are validated server-side with `typeof`, so
 * sending the string the input produced fails the save — for every field in the
 * same request, not just the offending one. Coercing here keeps one unparseable
 * answer from taking a whole category's worth of good ones down with it.
 */
export function toProfileValue(
  field: PlaceholderField,
  raw: string,
): { value: unknown } | null {
  if (!isProfileSavable(field)) return null;
  if (field.type === 'number') {
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? { value: parsed } : null;
  }
  if (field.type === 'boolean') {
    const normalized = raw.trim().toLowerCase();
    if (['true', 'yes', '1'].includes(normalized)) return { value: true };
    if (['false', 'no', '0'].includes(normalized)) return { value: false };
    return null;
  }
  return { value: raw };
}

/** Anything that is not a letter or a digit is noise for matching purposes. */
function normalize(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

export function hasValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value as object).length > 0;
  return true;
}

/**
 * Whether one blank still has no value — nothing typed for it, and no value
 * already in the profile for the field it matched.
 *
 * ADVISORY, NOT A GATE. Skipping a blank is allowed: the user may not know the
 * DPO's email today, and holding the whole run hostage to one unknown fact is
 * what left people stuck in this step. What this drives is what they are *told*
 * — the hint on the card, the count in the footer, the list in the submit
 * confirmation, the warning on the summary — so the choice to skip is made
 * rather than discovered later.
 *
 * Single-blank rather than only the list form, because the fill card asks the
 * same question about the one blank it draws. It was re-deriving the predicate
 * inline, which is how the card and the footer come to disagree about what
 * counts as answered.
 */
export function isBlank(placeholder: PendingPlaceholder, value: string | undefined): boolean {
  return (
    (value ?? '').trim().length === 0 &&
    !(placeholder.field && hasValue(placeholder.field.currentValue))
  );
}

/** Every blank of `placeholders` that {@link isBlank} still calls unanswered. */
export function unfilledPlaceholders(
  placeholders: PendingPlaceholder[],
  values: Record<string, string>,
): PendingPlaceholder[] {
  return placeholders.filter((placeholder) => isBlank(placeholder, values[placeholder.token]));
}

/**
 * Every blank the run's accepted documents still carry, matched to the profile
 * field that would fill it.
 *
 * Matching is on the token and the label, both normalized to letters and digits
 * — `[DPO_CONTACT_EMAIL]`, `DPO contact email` and `dpo_contact_email` are the
 * same question written three ways, and the AI, the catalog and the profile
 * template each pick a different one. An unmatched placeholder is still
 * reported: the user can see what is missing even when there is nowhere in the
 * profile to record the answer.
 */
export function collectPlaceholders(
  items: RemediationReviewItem[],
  template: LocalizedTemplateWithValues | undefined,
): PendingPlaceholder[] {
  const fieldsByKey = new Map<string, PlaceholderField>();
  for (const category of template?.categories ?? []) {
    for (const field of category.fields) {
      const entry: PlaceholderField = {
        id: field.id,
        key: field.key,
        label: field.label ?? field.key,
        type: field.type,
        options: field.options ?? null,
        currentValue: field.currentValue,
        categoryId: category.id,
      };
      fieldsByKey.set(normalize(field.key), entry);
      if (field.label) fieldsByKey.set(normalize(field.label), entry);
    }
  }

  const byToken = new Map<string, PendingPlaceholder>();
  for (const item of items) {
    // A document the reviewer rejected is not going into the new version, so
    // its blanks are not questions any more. Asking for them would be asking
    // the user to fill in something they just threw away.
    if (item.status === RemediationItemStatus.REJECTED) continue;

    const rejectedChanges = new Set(
      (item.changeSet && item.changeSet.mode !== 'WHOLE_DOCUMENT'
        ? item.changeSet.changes
        : []
      )
        .filter((change) => change.decision === RemediationDecision.REJECTED)
        .map((change) => change.changeId),
    );

    for (const placeholder of item.aiMetadata?.placeholdersToFill ?? []) {
      // The blank came in on one or more proposed changes. If every one of them
      // was rejected the text carrying it never lands, so neither does the
      // blank. `changeIds` is REWRITE-only — a generated document has no change
      // set, and its blanks stand or fall with the document itself, which the
      // status check above already decided.
      const introducedBy = placeholder.changeIds ?? [];
      if (introducedBy.length > 0 && introducedBy.every((id) => rejectedChanges.has(id))) {
        continue;
      }
      const documentName = item.documentName ?? '';
      const existing = byToken.get(placeholder.token);
      if (existing) {
        if (documentName && !existing.documents.includes(documentName)) {
          existing.documents.push(documentName);
        }
        continue;
      }
      const label = placeholder.label || placeholder.token;
      byToken.set(placeholder.token, {
        token: placeholder.token,
        label,
        documents: documentName ? [documentName] : [],
        field:
          fieldsByKey.get(normalize(placeholder.token)) ??
          fieldsByKey.get(normalize(label)) ??
          null,
      });
    }
  }

  return [...byToken.values()];
}

/**
 * The same placeholders, grouped by the document they appear in.
 *
 * Deduping by token stays underneath this: the page keys its answers by token,
 * so a `[DPO_CONTACT_EMAIL]` shared by four documents is typed once and reads
 * as filled on all four. The grouping is how the user walks them — one document
 * at a time, which is the unit they think in — not a second copy of the answer.
 *
 * A blank from an item with no `documentName` belongs to no named document, and
 * used to land in no group at all: it was counted as outstanding and had no
 * input anywhere to answer it. Those go under one final group instead, so every
 * blank the user is told about is one they can reach.
 *
 * That group's `documentName` is `null`, not a label. It is an identity — the
 * page matches items on it to pick the document to preview — so putting a
 * translated string there would make a group's identity change with the UI
 * language, and collide with a document genuinely called "Untitled document".
 * Naming it is the caller's job.
 */
export function groupByDocument(
  placeholders: PendingPlaceholder[],
): { documentName: string | null; placeholders: PendingPlaceholder[] }[] {
  const order: string[] = [];
  for (const placeholder of placeholders) {
    for (const documentName of placeholder.documents) {
      if (!order.includes(documentName)) order.push(documentName);
    }
  }
  const groups: { documentName: string | null; placeholders: PendingPlaceholder[] }[] =
    order.map((documentName) => ({
      documentName,
      placeholders: placeholders.filter((p) => p.documents.includes(documentName)),
    }));

  const orphans = placeholders.filter((p) => p.documents.length === 0);
  if (orphans.length > 0) groups.push({ documentName: null, placeholders: orphans });

  return groups;
}

